<script setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize, useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import BackButton from '../BackButton.vue';
import MoreActions from './MoreActions.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import SidepanelSwitch from 'dashboard/components-next/Conversation/SidepanelSwitch.vue';
import ContactStageChip from 'dashboard/components-next/Contacts/ContactDetail/ContactStageChip.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useAgentsList } from 'dashboard/composables/useAgentsList';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import {
  findListAttribute,
  TYPE_ATTRIBUTE_HINT,
  STAGE_ATTRIBUTE_HINT,
} from 'dashboard/components-next/Contacts/contactStageColors';
import { channelLabel } from 'dashboard/components-next/Contacts/contactChannel';

// Conversation header (CrmB): avatar · name + Tipo chip · "canal · teléfono
// · ciudad" on the left; Estado, assignee, Resolver and the rail toggle on
// the right.
const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox } = useInbox();
const { agentsList } = useAgentsList();

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta);

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat.meta.sender.id)
);

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);

// "WhatsApp · +51 933 184 191 · Lima, Surco"
const subtitle = computed(() => {
  const attributes = currentContact.value?.additional_attributes || {};
  const location = [attributes.city, attributes.country]
    .filter(Boolean)
    .join(', ');
  return [
    channelLabel(chatMetadata.value?.channel || inbox.value?.channel_type),
    inbox.value?.name && store.getters['inboxes/getInboxes'].length > 1
      ? inbox.value.name
      : null,
    currentContact.value?.phone_number,
    currentContact.value?.email,
    location,
  ]
    .filter(Boolean)
    .join(' · ');
});

// ---- Tipo / Estado chips (contact list attributes) ------------------------
const contactAttributes = useMapGetter('attributes/getContactAttributes');
const typeAttribute = computed(() =>
  findListAttribute(contactAttributes.value, 'tipo_contacto', TYPE_ATTRIBUTE_HINT)
);
const stageAttribute = computed(() =>
  findListAttribute(
    contactAttributes.value,
    'estado_contacto',
    STAGE_ATTRIBUTE_HINT
  )
);
const attributeValue = attribute =>
  attribute
    ? currentContact.value?.custom_attributes?.[attribute.attributeKey] || ''
    : '';

const updateAttribute = async (attribute, value) => {
  try {
    await store.dispatch('contacts/update', {
      id: currentContact.value.id,
      customAttributes: { [attribute.attributeKey]: value },
    });
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(
      error?.response?.message ||
        t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.API.UPDATE_ERROR')
    );
  }
};

// ---- Assignee pill ---------------------------------------------------------
const [showAssigneeMenu, toggleAssigneeMenu] = useToggle();
const assignee = computed(() => chatMetadata.value?.assignee || null);
const assigneeLabel = computed(
  () =>
    assignee.value?.name?.split(' ')[0] ||
    t('CONTACTS_LAYOUT.DETAILS.SUMMARY.UNASSIGNED')
);
const assigneeMenuItems = computed(() =>
  (agentsList.value || []).map(agent => ({
    label: agent.name,
    value: agent.id ?? 'none',
    action: 'assign',
    thumbnail: agent.id
      ? { name: agent.name, src: agent.thumbnail }
      : undefined,
    icon: agent.id ? undefined : 'i-lucide-user-x',
    isSelected: (assignee.value?.id ?? null) === (agent.id ?? null),
  }))
);
const onAssign = async ({ value }) => {
  toggleAssigneeMenu(false);
  const agentId = value === 'none' ? null : value;
  if ((assignee.value?.id ?? null) === agentId) return;
  const agent = (agentsList.value || []).find(a => a.id === agentId) || null;
  store.dispatch('setCurrentChatAssignee', agent?.id ? agent : null);
  await store.dispatch('assignAgent', {
    conversationId: currentChat.value.id,
    agentId,
  });
  useAlert(t('CONVERSATION.CHANGE_AGENT'));
};
</script>

<template>
  <div
    ref="conversationHeader"
    class="flex items-center gap-3 flex-shrink-0 w-full min-w-0 px-[18px] py-3 border-b border-n-weak bg-n-solid-2"
  >
    <BackButton
      v-if="showBackButton"
      :back-url="backButtonUrl"
      class="flex-shrink-0"
    />
    <Avatar
      :name="currentContact.name"
      :src="currentContact.thumbnail"
      :size="36"
      :status="currentContact.availability_status"
      hide-offline-status
    />
    <div class="flex flex-col min-w-0 gap-0.5 leading-tight">
      <div class="flex items-center gap-2 min-w-0">
        <span class="text-[14.5px] font-semibold truncate text-n-slate-12">
          {{ currentContact.name }}
        </span>
        <fluent-icon
          v-if="!isHMACVerified"
          v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
          size="14"
          class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
          icon="warning"
        />
        <ContactStageChip
          :attribute="typeAttribute"
          :value="attributeValue(typeAttribute)"
          show-dot
          show-chevron
          @update="updateAttribute(typeAttribute, $event)"
        />
      </div>
      <div class="flex items-center gap-2 min-w-0 text-[11.5px] text-n-slate-10">
        <span class="truncate">{{ subtitle }}</span>
        <span v-if="isSnoozed" class="flex-shrink-0 font-medium text-n-amber-10">
          {{ snoozedDisplayText }}
        </span>
      </div>
    </div>

    <div class="flex items-center flex-shrink-0 gap-2 ml-auto">
      <SLACardLabel
        v-if="hasSlaPolicyId"
        :chat="chat"
        show-extended-info
        :parent-width="width"
        class="hidden md:flex"
      />
      <ContactStageChip
        :attribute="stageAttribute"
        :value="attributeValue(stageAttribute)"
        variant="button"
        show-chevron
        align="right"
        class="hidden lg:block"
        @update="updateAttribute(stageAttribute, $event)"
      />
      <div
        v-on-clickaway="() => toggleAssigneeMenu(false)"
        class="relative hidden md:block"
      >
        <button
          type="button"
          class="inline-flex items-center gap-[7px] h-8 ltr:pl-1.5 ltr:pr-[11px] rtl:pr-1.5 rtl:pl-[11px] rounded-[7px] bg-n-alpha-2 text-[12.5px] font-medium text-n-slate-12 hover:bg-n-alpha-3 transition-colors max-w-[11rem]"
          :title="t('CONVERSATION.ASSIGNMENT.SELECT_AGENT')"
          @click="toggleAssigneeMenu()"
        >
          <Avatar
            v-if="assignee"
            :name="assignee.name"
            :src="assignee.thumbnail"
            :size="20"
          />
          <span
            v-else
            class="flex items-center justify-center size-5 rounded-[5px] bg-n-alpha-2 text-[10px] font-bold text-n-slate-10"
          >
            ?
          </span>
          <span class="truncate">{{ assigneeLabel }}</span>
        </button>
        <DropdownMenu
          v-if="showAssigneeMenu"
          :menu-items="assigneeMenuItems"
          show-search
          class="z-30 w-56 mt-1.5 top-full ltr:right-0 rtl:left-0 max-h-72 overflow-y-auto"
          @action="onAssign"
        />
      </div>
      <MoreActions :conversation-id="currentChat.id" />
      <SidepanelSwitch />
    </div>
  </div>
</template>
